import { z } from 'zod';

export const GranularidadeSchema = z.enum(['day', 'week', 'month']);

export const PeriodoFilterSchema = z
  .object({
    dataDe: z.iso.datetime().optional(),
    dataAte: z.iso.datetime().optional(),
    granularidade: GranularidadeSchema.default('day'),
  })
  // CR-03 fix: reject inverted ranges at schema parse time before they reach the
  // service. When both fields are present and dataDe > dataAte, previousRange
  // yields a negative span producing bogus null deltas with no visible error.
  .refine(
    (v) => !v.dataDe || !v.dataAte || new Date(v.dataDe) <= new Date(v.dataAte),
    { message: 'dataDe deve ser anterior a dataAte', path: ['dataDe'] },
  );

export const RelatorioGeralSchema = z.object({
  eventosRealizados: z.number(),
  profissionaisContratados: z.number(),
  visualizacoes: z.number().default(0),
  valorInvestidoCents: z.string(),
  deltaEventosRealizadosPct: z.number().nullable(),
  deltaProfissionaisContratadosPct: z.number().nullable(),
  deltaValorInvestidoPct: z.number().nullable(),
  // Phase 28 v1.6 — REQ-RELATORIOS-DELTAS-01 (D-15). Server-composed pt-BR
  // sentence summarizing the period vs the equivalent prior window. Always
  // present in the payload (no new endpoint — Pitfall 8 avoided). Frontend
  // renders via Angular interpolation (auto-escapes). Fallbacks:
  //   - prior period had no data → 'Comparação não disponível — período
  //     anterior sem dados.'
  //   - no stat has |delta| ≥ 5% → 'Variação estável em relação ao período
  //     anterior.'
  insight: z.string(),
  // Phase 31 v1.7 — DEBT-02. Approval rate for empresa's candidaturas in period.
  // calc: aprovadas.count / candidaturas.count (empresa scope, current period).
  // null = no candidaturas in period or no prior period available for delta.
  taxaAprovacao: z.number().min(0).max(100).nullable(),
  deltaTaxaAprovacaoPct: z.number().nullable(),
});

export const DesempenhoSerieSchema = z.object({
  buckets: z.array(z.string()),
  eventos: z.array(z.number()),
  contratacoes: z.array(z.number()),
  investimentoCents: z.array(z.string()),
});

export const PorStatusSchema = z.object({
  rascunho: z.number(),
  publicado: z.number(),
  em_andamento: z.number(),
  finalizado: z.number(),
  cancelado: z.number(),
});

export const PorCategoriaSchema = z.object({
  MONTADOR: z.number(),
  TECNICO: z.number(),
  CARREGADOR: z.number(),
});

export const TopEventoSchema = z.object({
  id: z.string(),
  nome: z.string(),
  investimentoCents: z.string(),
  contratacoes: z.number(),
});

export const TopEventosResponseSchema = z.object({
  items: z.array(TopEventoSchema),
});

export const TopEventosFilterSchema = z.object({
  limit: z.coerce.number().int().positive().max(20).default(5),
  por: z.enum(['investimento', 'contratacoes']).default('investimento'),
  dataDe: z.iso.datetime().optional(),
  dataAte: z.iso.datetime().optional(),
});

export type Granularidade = z.infer<typeof GranularidadeSchema>;
export type PeriodoFilter = z.infer<typeof PeriodoFilterSchema>;
export type RelatorioGeral = z.infer<typeof RelatorioGeralSchema>;
export type DesempenhoSerie = z.infer<typeof DesempenhoSerieSchema>;
export type PorStatus = z.infer<typeof PorStatusSchema>;
export type PorCategoria = z.infer<typeof PorCategoriaSchema>;
export type TopEvento = z.infer<typeof TopEventoSchema>;
export type TopEventosResponse = z.infer<typeof TopEventosResponseSchema>;
export type TopEventosFilter = z.infer<typeof TopEventosFilterSchema>;
